import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Trash, Plus, RotateCcw } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./components/ui/form";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import React, { useMemo, useState } from "react";

import { EditableDataTable } from "./components/EditableDataTable";
import { DialogDrawer } from "./components/DialogDrawer";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { Checkbox } from "./components/ui/checkbox";
import {
  defaultExpenseData,
  initialExpensesData,
  ExpenseDataSchema,
  type ExpenseType,
  FrequencyTypeArr,
} from "./data";
import {
  useEditableTable,
  type EditableColumnDef,
} from "./hooks/useEditableTable";
import { Switch } from "./components/ui/switch";
import type {
  CreateExpenseHandler,
  DeleteExpenseHandler,
  UpdateExpenseHandler,
} from "./hooks/useExpenseReducer";
import { calculateTotalExpensesPerYear } from "./lib/calculators";
import { ResetButton } from "./components/ResetButton";
import { SubSectionCard } from "./components/SubSectionCard";

const expensesDataColumns: EditableColumnDef<ExpenseDataSchema>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    meta: {
      editable: false,
    },
  },
  {
    accessorKey: "amount",
    header: "Amount ($)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "frequency",
    header: "Frequency",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "essential",
    header: "Essential",
    cell: ({ cell }) => {
      const value = cell.getValue();
      return value === true ? "Yes" : "No";
    },
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "description",
    header: "Description",
    meta: {
      editable: true,
    },
  },
  {
    id: "actions",
    meta: {
      editable: false,
    },
    cell: ({ table, row }) => (
      <Button
        variant="ghost"
        className="h-8 w-8 p-0"
        onClick={() => {
          table.options.meta?.deleteRow(row);
        }}
      >
        <span className="sr-only">Remove</span>
        <Trash className="h-4 w-4" />
      </Button>
    ),
  },
];

interface ExpensesFormProps {
  initialValues: ExpenseDataSchema;
  onFormSubmit: (values: ExpenseDataSchema) => void;
  onFormCancel: () => void;
}

function ExpensesForm({
  initialValues,
  onFormSubmit,
  onFormCancel,
}: ExpensesFormProps) {
  const form = useForm<ExpenseDataSchema>({
    resolver: zodResolver(ExpenseDataSchema),
    values: initialValues,
  });

  function onSubmitHandler(values: ExpenseDataSchema) {
    onFormSubmit(values);
  }

  function onCancelHandler() {
    form.reset();
    onFormCancel();
  }

  const numToInputString = (num: number) =>
    num === 0 ? "" : num.toString();

  // function strToNum(str: string, fallback: number = 0) {
  //   const output = parseFloat(str);
  //   return isNaN(output) ? fallback : output;
  // }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-8">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  value={numToInputString(field.value)}
                  onChange={(e) => field.onChange(+e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Frequency</FormLabel>
              <FormControl>
                <Select {...field} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {FrequencyTypeArr.map((val) => (
                      <SelectItem value={val}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="essential"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Essential</FormLabel>
              <FormControl>
                <Switch
                  name={field.name}
                  disabled={field.disabled}
                  onBlur={field.onBlur}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full mb-2">
          Save
        </Button>
        <Button
          type="button"
          className="w-full"
          variant="secondary"
          onClick={onCancelHandler}
        >
          Cancel
        </Button>
      </form>
    </Form>
  );
}

interface DialogDrawerExpensesFormProps extends React.ComponentProps<"div"> {
  data: ExpenseDataSchema;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFormSubmit: (data: ExpenseDataSchema) => void;
}

function DialogDrawerExpensesForm({
  data,
  onFormSubmit,
  open,
  onOpenChange,
}: DialogDrawerExpensesFormProps) {
  const handleFormSubmit = (data: ExpenseDataSchema) => {
    onFormSubmit(data);
    onOpenChange(false);
  };

  return (
    <DialogDrawer
      title="Edit expense"
      description="Make changes to your expense here. Click save when you're done."
      onOpenChange={onOpenChange}
      open={open}
    >
      <ExpensesForm
        initialValues={data}
        onFormSubmit={handleFormSubmit}
        onFormCancel={() => onOpenChange(false)}
      />
    </DialogDrawer>
  );
}

interface ExpenseSubSectionProps {
  title: string;
  description?: string;
  expenseType: ExpenseType;
  data: ExpenseDataSchema[];
  onCreate: CreateExpenseHandler;
  onUpdate: UpdateExpenseHandler;
  onDelete: DeleteExpenseHandler;
}

function ExpenseSubSection({
  title,
  description,
  expenseType,
  data,
  onCreate,
  onUpdate,
  onDelete,
}: ExpenseSubSectionProps) {
  const [selectedRow, setSelectedRow] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const rowSelection = useMemo(
    () =>
      data.reduce((acc, entry, idx) => {
        return {
          ...acc,
          [idx]: entry.selected,
        };
      }, {} as RowSelectionState),
    [data]
  );
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (
    rowSelectionUpdater
  ) => {
    const newRowSelection =
      typeof rowSelectionUpdater === "function"
        ? rowSelectionUpdater(rowSelection)
        : rowSelectionUpdater;
    const newData = data.map((entry, idx) => ({
      ...entry,
      selected: newRowSelection[idx] || false,
    }));
    onUpdate(expenseType, newData);
  };

  const table = useEditableTable<ExpenseDataSchema>({
    columns: expensesDataColumns,
    data,
    onCreate: () => onCreate(expenseType, defaultExpenseData()),
    onDelete: (row) => {
      const entry = data[row.index];
      if (entry) {
        onDelete(expenseType, entry);
      }
    },
    rowSelection,
    onRowSelectionChange,
  });

  const totalExpensesPerYear = calculateTotalExpensesPerYear(data);

  return (
    <>
      {dialogOpen && (
        <DialogDrawerExpensesForm
          onFormSubmit={(newData) => onUpdate(expenseType, newData)}
          data={data[selectedRow] || defaultExpenseData()}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
      <SubSectionCard
        title={
          <div className="flex flex-row">
            <div className="grow">{title}</div>
            <ResetButton
              onClick={() => {
                onDelete(expenseType, data);
                onCreate(expenseType, initialExpensesData[expenseType]);
              }}
            />
          </div>
        }
        description={description}
      >
        <EditableDataTable
          table={table}
          onCellClick={(cell) => {
            setSelectedRow(cell.row.index);
            setDialogOpen(true);
          }}
        />
        <Button
          variant="ghost"
          className="h-8 w-full p-0"
          onClick={() => {
            onCreate(expenseType, defaultExpenseData());
          }}
        >
          <span className="sr-only">Add</span>
          <Plus className="h-4 w-4" />
        </Button>
        <div className="flex-col items-start gap-1 text-md">
          <div className="text-muted-foreground">
            ${+(totalExpensesPerYear.total / 52).toFixed(2)} per week
          </div>
          <div className="text-muted-foreground">
            ${+(totalExpensesPerYear.total / 26).toFixed(2)} per fortnight
          </div>
          <div className="text-muted-foreground">
            ${+(totalExpensesPerYear.total / 12).toFixed(2)} per month
          </div>
        </div>
      </SubSectionCard>
    </>
  );
}

interface ExpensesSectionProps {
  expensesData: Record<ExpenseType, ExpenseDataSchema[]>;
  onCreate: CreateExpenseHandler;
  onUpdate: UpdateExpenseHandler;
  onDelete: DeleteExpenseHandler;
}

export function ExpensesSection({
  expensesData,
  onCreate,
  onUpdate,
  onDelete,
}: ExpensesSectionProps) {
  const expenseTitles: Record<ExpenseType, string> = useMemo(
    () => ({
      rent: "Rent",
      utilities: "Utilities",
      transport: "Transport & Auto",
      insurance: "Insurance",
      loans: "Loans",
      medical: "Medical",
      personal: "Personal & Leisure",
      food: "Food",
      education: "Education",
      children: "Children",
      other: "Other",
    }),
    []
  );

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(expensesData).map(([expenseType, data]) => {
        return (
          <ExpenseSubSection
            key={expenseType}
            expenseType={expenseType as ExpenseType}
            title={expenseTitles[expenseType as ExpenseType]}
            description="Expense"
            data={data}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        );
      })}
    </div>
  );
}
